import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import {
  Form, Input, Button, Row, Col, Space, Alert, Select,
} from 'antd';

import PageContent from '../../components/Layout/PageContent';
import {
  webCardsGet, webCardUpdate, webCardCreate, webCardSort,
} from '../../redux/actions/webCards';
import {
  loadingSelector, errorsSelector, dataSelector, dataIdsLengthSelector,
} from '../../redux/selectors/webCards';
import { validateMaxLength } from '../../helpers/form';
import './WebCards.css';

const WebCardsForm = () => {
  const { id } = useParams();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const loading = useSelector(loadingSelector);
  const errors = useSelector(errorsSelector);
  const webCardsLength = useSelector(dataIdsLengthSelector) + (id ? 0 : 1);
  const webCard = useSelector(dataSelector)(id);

  const onPublish = async (values) => {
    if (id) {
      if (values.order !== webCard.order) {
        await dispatch(webCardSort(webCard.order - 1, values.order - 1));
      }
      dispatch(webCardUpdate(id, values));
    } else {
      dispatch(webCardCreate(values));
    }
  };

  useEffect(() => {
    dispatch(webCardsGet());
  }, [dispatch, id]);

  useEffect(() => {
    form.setFieldsValue(webCard);
  }, [form, webCard]);

  return (
    <PageContent title="Current Web Cards" breadcrumb={['Web', 'Web Cards']} loading={loading}>
      {errors && <Alert message={errors} type="error" showIcon closable />}
      <Form
        form={form}
        layout="vertical"
        initialValues={webCard}
        validateMessages={{ required: 'This field is required', types: { url: 'Enter a valid url' } }}
        onFinish={onPublish}
        scrollToFirstError
      >
        <Form.Item
          label="Title"
          name="title"
          extra="45 characters limit"
          rules={[{
            validator: validateMaxLength,
            maxLength: 45,
          }]}
        >
          <Input size="large" placeholder="Title copy" />
        </Form.Item>
        <Form.Item
          label="Subtext"
          name="subText"
          extra="170 characters limit"
          rules={[{
            validator: validateMaxLength,
            maxLength: 170,
          }]}
        >
          <Input.TextArea placeholder="Subtext copy" />
        </Form.Item>
        <h3>Image Assets</h3>
        <Form.Item
          label="Desktop Image URL"
          name="desktopImageURL"
          rules={[{ required: true, type: 'url' }]}
          required
        >
          <Input size="large" placeholder="https://" />
        </Form.Item>
        <Form.Item
          label="Tablet Image URL"
          name="tabletImageURL"
          rules={[{ required: true, type: 'url' }]}
          required
        >
          <Input size="large" placeholder="https://" />
        </Form.Item>
        <Form.Item
          label="Mobile Image URL"
          name="mobileImageURL"
          rules={[{ required: true, type: 'url' }]}
          required
        >
          <Input size="large" placeholder="https://" />
        </Form.Item>
        <h3>CTA Button</h3>
        <Row gutter={27}>
          <Col span={12}>
            <Form.Item
              label="Link Address"
              name="ctaLink"
              rules={[{ type: 'url', required: true }]}
              required
            >
              <Input size="large" placeholder="https://" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="CTA Label"
              name="ctaLabel"
              rules={[
                { required: true },
                { validator: validateMaxLength, maxLength: 25 },
              ]}
              placeholder="CTA copy"
              extra="25 characters limit"
              required
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="Card Order"
          name="order"
          rules={[{ required: true }]}
          initialValue={webCard && webCard.order ? undefined : webCardsLength}
          style={{ width: 86 }}
          required
        >
          <Select disabled={!id}>
            {Array(webCardsLength).fill().map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Select.Option key={index} value={index + 1}>{index + 1}</Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item wrapperCol={{ gutter: 16 }} style={{ marginTop: 50 }}>
          <Space>
            <Button type="primary" size="large" htmlType="submit">Publish</Button>
            <Button type="secondary" size="large">Review</Button>
            <Link to="/cards"><Button type="link" size="large">Cancel</Button></Link>
          </Space>
        </Form.Item>
      </Form>
    </PageContent>
  );
};

export default WebCardsForm;
